import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';

import { Input, InputNumber, Space, Button, Form, Select, Tooltip, Switch, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import FormHeader from '@controls/form-header/form-header';
import Site from '@components/site/site';
import Tag from '@components/tag/tag';

import { getEnumList } from '@extensions/utils';

import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { IConsignee } from '@entities/consignee';
import { ICity } from '@entities/city';
import { IUserFilter } from '@entities/user-filter';
import { IUser } from '@entities/user';
import { ICountry } from '@entities/country';
import { IConsigneeTariffSetting } from '@entities/consignee-tariff-setting';

import { UserType } from '@enums/user-type';
import { ConsigneeType, enumLabel as consigneeEnumLabel } from '@enums/consignee-type';
import { Permission, hasPermission } from '@enums/permission';

import { ISite } from '@core/entities/site';

const Consignee = () => {
    const { id } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();

    const { TextArea } = Input;

    const [form] = Form.useForm();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<IConsignee>({
        id: undefined,
        userId: undefined,
        type: ConsigneeType.Main,
        cityId: undefined,
        isArchived: false,
        tariffSettings: [],
    });

    const [consigneeTypes] = useState(getEnumList(ConsigneeType, consigneeEnumLabel));
    const [loading, setLoading] = useState<boolean>(false);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [users, setUsers] = useState<Array<IUser>>([]);
    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageConsignees));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const userFilter: IUserFilter = { isActive: undefined /*true*/, isArchived: false, types: [UserType.Supplier] };

            const promises = [
                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('users', { method: 'GET', queryParams: userFilter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (id) {
                promises.push(
                    await serverFetch(`consignees/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения грузополучателя', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCities(result[0][0]);
                setUsers(result[0][1]);

                const consignee = result[0][2] as IConsignee;
                if (consignee) {
                    if (consignee.tag) {
                        try {
                            consignee.tags = JSON.parse(consignee.tag);
                        } catch {
                            consignee.tags = [];
                            consignee.tags.push(consignee.tag);
                        }
                    }
                }
                setEntity({ ...consignee });

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, []);

    const onSave = () => {
        setLoading(true);

        entity.tag = JSON.stringify(entity.tags);

        serverFetch(`consignees`, { method: id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };

    const onRestore = () => {
        serverFetch(`consignees/${id}/restore`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка восстановления грузополучателя', ex, () => d(userLoaded(undefined)));
            });
    };
    return (
        <>
            <FormHeader
                title={`${id ? (isViewOnly ? 'Информация о грузополучателе' : 'Изменить грузополучателя') : 'Добавить грузополучателя'}`}
            />
            {!loading && (
                <Form preserve={false} colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 8 }} form={form} onFinish={onSave}>
                    <Form.Item
                        initialValue={entity?.code}
                        validateFirst={true}
                        label={
                            <>
                                ID
                                <Tooltip title='Поле ID сгенерируется атоматически'>
                                    <InfoCircleOutlined style={{ marginLeft: 5 }} />{' '}
                                </Tooltip>
                            </>
                        }
                        name='code'
                        rules={[
                            {
                                validator: async (_, value: number) => {
                                    const isCodeExist = await serverFetch(`consignees/codeexist/${id}/${value}`, { method: 'GET' })
                                        .then((data) => {
                                            return data;
                                        })
                                        .catch((ex) => {
                                            exception(api, 'Ошибка проверки кода', ex, () => d(userLoaded(undefined)));
                                        });

                                    if (isCodeExist) return Promise.reject('Грузополучатель с указанным кодом уже существует');
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <InputNumber
                            disabled={isViewOnly || !!entity.id || !hasPermission(userSession.permissions, Permission.FullAccess)}
                            onChange={(value: number | null) => {
                                setEntity({ ...entity, code: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.keyName} label='Псевдоним' name='keyName'>
                        <Input
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                            disabled={isViewOnly}
                            onChange={(data) => {
                                setEntity({ ...entity, keyName: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.cityId}
                        required
                        label='Город доставки'
                        name='city'
                        rules={[{ required: true, message: 'Выберите город доставки' }]}
                    >
                        <Select
                            disabled={isViewOnly}
                            onChange={(value) => setEntity({ ...entity, cityId: value })}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.tags} label='Тег' name='tag'>
                        <Tag
                            initialTags={entity.tags || []}
                            isEditMode={!isViewOnly}
                            api={api}
                            onSelect={(e: Array<string> | undefined) => {
                                setEntity({ ...entity, tags: e });
                            }}
                            serverFetch={serverFetch}
                        />
                    </Form.Item>
                    <Form.Item initialValue={entity.siteId} label='Адрес разгрузки' name='siteId'>
                        <Site
                            cityId={entity.cityId}
                            siteId={entity.siteId}
                            isEditMode={!isViewOnly}
                            api={api}
                            onSelect={(e: ISite | undefined) => {
                                setEntity({ ...entity, siteId: e?.id });
                            }}
                            serverFetch={serverFetch}
                        />
                    </Form.Item>
                    <Form.Item
                        initialValue={entity.fullName}
                        required
                        label='ФИО'
                        name='fullName'
                        rules={[{ required: true, message: 'Укажите ФИО' }]}
                    >
                        <Input
                            disabled={isViewOnly}
                            onChange={(data) => {
                                setEntity({ ...entity, fullName: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <PhoneInputFormItem
                        value={entity?.phone}
                        name='phone'
                        label='Телефон'
                        required={true}
                        disabled={isViewOnly}
                        onChange={(value) => {
                            setEntity({ ...entity, phone: value });
                        }}
                    />

                    <Form.Item label='Скрыть телефон' initialValue={entity?.hidePhone} valuePropName='checked' name='hidePhone'>
                        <Switch
                            disabled={isViewOnly}
                            onChange={(value) => {
                                setEntity({ ...entity, hidePhone: value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 3, span: 8 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {!isViewOnly &&
                                (entity.isArchived ? (
                                    (hasPermission(userSession.permissions, Permission.FullAccess) ||
                                        hasPermission(userSession.permissions, Permission.RestoreConsignee)) && (
                                        <Button type='primary' onClick={() => onRestore()}>
                                            Восстановить
                                        </Button>
                                    )
                                ) : (
                                    <Button type='primary' htmlType='submit' loading={loading}>
                                        Сохранить
                                    </Button>
                                ))}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
        </>
    );
};

export default Consignee;
